import { IconName } from '@bytel/trilogy-react-ts';
import {
  ACTUALITES_LIST_PATH,
  CONTRIBUTION_NEUTRALITE_PATH,
  ECOASTUCES_LIST_PATH,
  PARTENAIRES_PAGE_PATH,
} from '@router/paths';
import { RouteParameters } from '@router/types';
import { BtnNavigationProps } from '@shared/components/button-navigation/type';
import { GLOBAL_CONSO_ID } from '@shared/constants';
import {
  useListerActualites,
  useListerEcoAstuces,
  useListerPartenaires,
  useObtenirConsommationFlotte,
  useObtenirEmpreintesCarboneEquipements,
} from '@shared/contexts/api';
import { useSiren } from '@shared/contexts/siren-context';
import UrlToken from '@shared/misc/helpers/urlToken';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseHomeProps } from './type';

const useData = (): UseHomeProps => {
  const { t } = useTranslation('domain/home', {
    keyPrefix: 'navButtons',
  });

  const siren = useSiren();

  const buttonNeutraliteCarbone = useMemo<BtnNavigationProps>(() => {
    return {
      dataCy: 'home-neutralite-btn',
      titre: t('myNeutralite'),
      description: t('myNeutraliteDesc'),
      routeRef: {
        href: CONTRIBUTION_NEUTRALITE_PATH.replace(
          RouteParameters.sirenId,
          UrlToken.fromXLink(siren?._links?.listerActualites).toString(),
        ),
      },
      iconName: IconName.HANDS_HELPING,
    };
  }, [t, siren?._links?.listerActualites]);

  const { data } = useObtenirConsommationFlotte(siren?._links?.obtenirConsommationFlotte?.href);
  const { data: empreintesData } = useObtenirEmpreintesCarboneEquipements({
    siren: siren?.id !== GLOBAL_CONSO_ID ? siren?.id : undefined,
    raisonSociale: siren?.raisonSociale,
  });
  const { data: ecoAstuces } = useListerEcoAstuces(siren?._links?.listerEcoAstuces?.href);
  const { data: partenaires } = useListerPartenaires(siren?._links?.listerPartenaires?.href);
  const { data: actualites } = useListerActualites(siren?._links?.listerActualites?.href);

  const buttons = useMemo<BtnNavigationProps[]>(() => {
    return [
      {
        dataCy: 'home-ecoAstuces-btn',
        titre: t('myEcoAstuces'),
        routeRef: siren?._links?.listerEcoAstuces
          ? {
              href: ECOASTUCES_LIST_PATH.replace(
                RouteParameters.sirenId,
                UrlToken.fromXLink(siren?._links?.listerEcoAstuces).toString(),
              ),
            }
          : undefined,
        count: ecoAstuces?.resultsCount,
        iconName: IconName.ENVIRONMENTAL_FOOTPRINT,
      },
      {
        dataCy: 'home-actualites-btn',
        titre: t('myActualite'),
        routeRef: siren?._links?.listerActualites
          ? {
              href: ACTUALITES_LIST_PATH.replace(
                RouteParameters.sirenId,
                UrlToken.fromXLink(siren?._links?.listerActualites).toString(),
              ),
            }
          : undefined,
        count: actualites?.resultsCount,
        iconName: IconName.FILE_LIST,
      },
      {
        dataCy: 'home-partenaires-btn',
        titre: t('myPartenaire'),
        routeRef: siren?._links?.listerPartenaires
          ? {
              href: PARTENAIRES_PAGE_PATH.replace(
                RouteParameters.sirenId,
                // Fonctionne pas encore il faut le modifier
                UrlToken.fromXLink(siren?._links?.listerPartenaires).toString(),
              ),
            }
          : undefined,
        count: partenaires?.resultsCount,
        iconName: IconName.COMMUNITY,
      },
    ];
  }, [
    t,
    siren?._links?.listerEcoAstuces,
    siren?._links?.listerActualites,
    siren?._links?.listerPartenaires,
    ecoAstuces?.resultsCount,
    actualites?.resultsCount,
    partenaires?.resultsCount,
  ]);

  const isShowIndicators = useMemo<boolean>(() => {
    return !!(siren?.id !== GLOBAL_CONSO_ID);
  }, [siren?.id]);

  return {
    buttons,
    buttonNeutraliteCarbone,
    dataConsommation: data,
    dataEmpreintes: empreintesData,
    isShowIndicators,
  };
};

export default useData;
